import type { EChartsOption } from 'echarts';

import type {
  TimeRangeLane,
  TimeSeriesChartProps,
  TimeSeriesChartRangeTrack,
} from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { PLOT_X_AXIS_INDEX, TRACK_X_AXIS_INDEX } from './config/chartLayout';
import { animationVisuals } from './config/chartVisuals';
import { buildGridOption } from './layout/buildGridOption';
import { buildSectionLabelOption, getVisibleSectionLabels } from './layout/buildSectionLabelOption';
import { buildNavigationOption } from './navigation/buildNavigationOption';
import { buildPlotOption } from './plot/buildPlotOption';
import { buildRangeTrackOption } from './range-track/buildRangeTrackOption';
import { buildTooltipAnchorOption } from './tooltip/options/buildTooltipAnchorOption';
import { buildTooltipOption } from './tooltip/options/buildTooltipOption';
import type { ChartFormatters } from './types/ChartFormatters.types';
import type { LineViewport } from './types/LineViewport.types';
import { getChartFormatters } from './utils/chartFormatters';
import { composeOptionSections } from './utils/composeOptionSections';
import { toTimeDomain } from './utils/timeDomain';

export type BuildChartOptionParams = Omit<TimeSeriesChartProps, 'accessibility'> & {
  theme: TimeSeriesChartTheme;
  renderTooltipContent: (formatters: ChartFormatters) => string;
  /** The settled time window and plot width the lines are drawn for (see LineViewport). */
  viewport: LineViewport;
};

/** The lanes the highlight names, in lane order; none without a range track or a lane highlight. */
function getHighlightedLanes(
  rangeTrack: TimeSeriesChartRangeTrack | undefined,
  laneIds: string[] = []
): TimeRangeLane[] {
  const highlightedLaneIds = new Set(laneIds);

  return rangeTrack?.lanes.filter(({ id }) => highlightedLaneIds.has(id)) ?? [];
}

export function buildChartOption({
  series,
  rangeTrack,
  timeDomain,
  highlight,
  navigation,
  isTooltipEnabled = false,
  sectionLabels,
  valueAxisScales,
  locale,
  theme,
  renderTooltipContent,
  viewport,
}: BuildChartOptionParams): EChartsOption {
  const formatters = getChartFormatters(locale);
  const highlightedLanes = getHighlightedLanes(rangeTrack, highlight?.laneIds);
  const laneCount = rangeTrack?.lanes.length ?? 0;
  const hasNavigation = navigation !== undefined;
  const { plotLabel, rangeTrackLabel } = getVisibleSectionLabels(sectionLabels, laneCount);
  const domain = toTimeDomain(timeDomain);

  const plot = buildPlotOption({
    series,
    highlightedLanes,
    highlight,
    domain,
    isGuideLineEnabled: isTooltipEnabled,
    viewport,
    previewSeriesId: navigation?.previewSeriesId,
    hasSliderPreview: hasNavigation,
    valueAxisScales,
    formatters,
    theme,
  });

  const hasTrack = rangeTrack !== undefined && laneCount > 0;

  // Order matters: the plot and the track own x axes 0 and 1 (see chartLayout), and the tooltip
  // anchor's axes come last, outside the data zoom.
  const {
    xAxis,
    yAxis,
    series: allSeries,
  } = composeOptionSections([
    plot,
    hasTrack ? buildRangeTrackOption({ rangeTrack, highlightedLanes, domain, theme }) : undefined,
    isTooltipEnabled ? buildTooltipAnchorOption() : undefined,
  ]);

  const navigableXAxisIndexes = hasTrack ? [PLOT_X_AXIS_INDEX, TRACK_X_AXIS_INDEX] : [PLOT_X_AXIS_INDEX];

  return {
    animation: true,
    animationDuration: animationVisuals.initialDurationMs,
    animationDurationUpdate: animationVisuals.updateDurationMs,

    grid: buildGridOption(laneCount, hasNavigation, rangeTrackLabel !== undefined),
    graphic: buildSectionLabelOption({ plotLabel, rangeTrackLabel, laneCount, hasNavigation, theme }),

    xAxis,
    yAxis,
    series: allSeries,

    // The guide line must move on both time axes at once, otherwise it only appears in the plot.
    ...(isTooltipEnabled &&
      hasTrack && {
        axisPointer: {
          link: [{ xAxisIndex: navigableXAxisIndexes }],
        },
      }),

    ...(hasNavigation && buildNavigationOption({ xAxisIndexes: navigableXAxisIndexes, theme })),
    ...(isTooltipEnabled && buildTooltipOption(() => renderTooltipContent(formatters))),
  };
}
